<template>
  <div>
    <SiteHeader :nav-items="navItems" :show-search="false" />

    <main class="bg-stone-50 pb-16">
      <section class="bg-[radial-gradient(circle_at_top_left,_rgba(255,126,0,0.16),_transparent_36%),linear-gradient(180deg,#fff8f1_0%,#fafaf9_100%)]">
        <div class="mx-auto grid max-w-7xl gap-8 px-4 py-10 md:grid-cols-[1.02fr_0.98fr] md:py-16">
          <div v-scroll-reveal="'fade-right'" class="flex flex-col justify-center">
            <div class="inline-flex w-fit rounded-full border border-brand-200 bg-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.22em] text-brand-700">
              {{ content.hero.badge }}
            </div>
            <h1 class="mt-5 max-w-3xl text-4xl font-semibold leading-tight text-stone-900 md:text-6xl">{{ content.hero.heading }}</h1>
            <p class="mt-5 max-w-2xl text-lg leading-8 text-stone-700">{{ content.hero.subheading }}</p>
            <p class="mt-5 max-w-3xl text-base leading-8 text-stone-600 md:text-lg">{{ content.hero.description }}</p>
            <div class="mt-8 flex flex-wrap gap-3">
              <RouterLink to="/" class="rounded-full bg-brand-500 px-6 py-3.5 font-semibold text-white shadow-float transition hover:bg-brand-600">
                {{ content.hero.ctaPrimary }}
              </RouterLink>
              <a href="/#contact" class="rounded-full border border-stone-300 bg-white px-6 py-3.5 font-semibold text-stone-700 shadow-soft transition hover:border-brand-300 hover:text-brand-600">
                {{ content.hero.ctaSecondary }}
              </a>
            </div>
          </div>

          <div v-scroll-reveal="{ variant: 'fade-left', delay: 120 }" class="overflow-hidden rounded-[2rem] border border-stone-200 bg-white p-4 shadow-soft">
            <div v-scroll-reveal-stagger class="grid min-h-[420px] gap-4 rounded-[1.6rem] bg-gradient-to-br from-orange-100 via-brand-50 to-white p-6 md:grid-cols-2">
              <div v-for="item in content.hero.metrics" :key="item.title" class="rounded-[1.4rem] bg-white/85 p-5 shadow-soft">
                <div class="text-3xl font-semibold text-stone-900">{{ item.value }}</div>
                <div class="mt-2 text-sm font-medium text-stone-500">{{ item.title }}</div>
                <p class="mt-3 text-sm leading-7 text-stone-600">{{ item.description }}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section class="mx-auto max-w-7xl px-4 py-12 md:py-16">
        <div class="grid gap-6 md:grid-cols-[0.9fr_1.1fr]">
          <div v-scroll-reveal="'fade-right'" class="rounded-[2rem] border border-stone-200 bg-white p-6 shadow-soft md:p-8">
            <div class="text-sm font-semibold uppercase tracking-[0.24em] text-brand-600">{{ content.story.badge }}</div>
            <h2 class="mt-3 text-3xl font-semibold text-stone-900 md:text-4xl">{{ content.story.heading }}</h2>
            <p class="mt-5 text-base leading-8 text-stone-600 md:text-lg">{{ content.story.description }}</p>
          </div>

          <div v-scroll-reveal-stagger="{ variant: 'fade-left' }" class="grid gap-4">
            <article v-for="point in content.story.points" :key="point.title" class="rounded-[2rem] border border-stone-200 bg-white p-6 shadow-soft">
              <div class="flex items-start gap-4">
                <span class="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-brand-50 text-brand-600">
                  <AppIcon :name="point.icon" class="h-7 w-7" />
                </span>
                <div>
                  <h3 class="text-xl font-semibold text-stone-900">{{ point.title }}</h3>
                  <p class="mt-2 text-sm leading-7 text-stone-600">{{ point.description }}</p>
                </div>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section class="bg-white py-12 md:py-16">
        <div class="mx-auto max-w-7xl px-4">
          <div v-scroll-reveal class="mb-8 text-center">
            <div class="text-sm font-semibold uppercase tracking-[0.24em] text-brand-600">{{ content.scope.badge }}</div>
            <h2 class="mt-3 text-3xl font-semibold text-stone-900 md:text-4xl">{{ content.scope.heading }}</h2>
            <p class="mx-auto mt-4 max-w-3xl text-base leading-8 text-stone-600 md:text-lg">{{ content.scope.description }}</p>
          </div>

          <div v-scroll-reveal-stagger class="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <article v-for="item in content.scope.items" :key="item.title" class="rounded-[2rem] border border-stone-200 bg-stone-50 p-6 shadow-soft">
              <div class="grid h-14 w-14 place-items-center rounded-2xl bg-white text-brand-600 shadow-soft">
                <AppIcon :name="item.icon" class="h-7 w-7" />
              </div>
              <h3 class="mt-5 text-xl font-semibold text-stone-900">{{ item.title }}</h3>
              <p class="mt-3 text-sm leading-7 text-stone-600">{{ item.description }}</p>
              <ul class="mt-4 grid gap-2 text-sm text-stone-500">
                <li v-for="point in item.points" :key="point" class="flex gap-2">
                  <span class="text-brand-500">•</span>
                  <span>{{ point }}</span>
                </li>
              </ul>
            </article>
          </div>
        </div>
      </section>

      <section class="mx-auto max-w-7xl px-4 py-12 md:py-16">
        <div v-scroll-reveal class="mb-8">
          <div class="text-sm font-semibold uppercase tracking-[0.24em] text-brand-600">{{ content.values.badge }}</div>
          <h2 class="mt-3 text-3xl font-semibold text-stone-900 md:text-4xl">{{ content.values.heading }}</h2>
          <p class="mt-4 max-w-3xl text-base leading-8 text-stone-600 md:text-lg">{{ content.values.description }}</p>
        </div>

        <div v-scroll-reveal-stagger class="grid gap-4 md:grid-cols-3">
          <article v-for="value in content.values.items" :key="value.title" class="rounded-[2rem] border border-stone-200 bg-white p-6 shadow-soft">
            <h3 class="text-xl font-semibold text-stone-900">{{ value.title }}</h3>
            <p class="mt-3 text-sm leading-7 text-stone-600">{{ value.description }}</p>
          </article>
        </div>
      </section>

      <section class="bg-white py-12 md:py-16">
        <div class="mx-auto max-w-7xl px-4">
          <div v-scroll-reveal class="mb-8 text-center">
            <div class="text-sm font-semibold uppercase tracking-[0.24em] text-brand-600">{{ content.process.badge }}</div>
            <h2 class="mt-3 text-3xl font-semibold text-stone-900 md:text-4xl">{{ content.process.heading }}</h2>
          </div>

          <div v-scroll-reveal-stagger class="grid gap-4 md:grid-cols-4">
            <article v-for="(step, index) in content.process.steps" :key="step.title" class="rounded-[2rem] border border-stone-200 bg-stone-50 p-6 shadow-soft">
              <div class="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-500 text-sm font-semibold text-white">
                {{ String(index + 1).padStart(2, "0") }}
              </div>
              <h3 class="mt-5 text-xl font-semibold text-stone-900">{{ step.title }}</h3>
              <p class="mt-2 text-sm leading-7 text-stone-600">{{ step.description }}</p>
            </article>
          </div>
        </div>
      </section>

      <section class="mx-auto max-w-7xl px-4 py-12 md:py-16">
        <div class="grid gap-4 md:grid-cols-2">
          <div v-scroll-reveal="'fade-right'" class="rounded-[2rem] border border-stone-200 bg-white p-6 shadow-soft md:p-8">
            <div class="text-sm font-semibold uppercase tracking-[0.24em] text-brand-600">{{ content.afterSales.badge }}</div>
            <h2 class="mt-3 text-3xl font-semibold text-stone-900 md:text-4xl">{{ content.afterSales.heading }}</h2>
            <p class="mt-4 text-base leading-8 text-stone-600 md:text-lg">{{ content.afterSales.description }}</p>
          </div>

          <div v-scroll-reveal-stagger="{ variant: 'fade-left' }" class="grid gap-4">
            <article v-for="item in content.afterSales.items" :key="item.title" class="rounded-[2rem] border border-stone-200 bg-white p-6 shadow-soft">
              <h3 class="text-xl font-semibold text-stone-900">{{ item.title }}</h3>
              <p class="mt-2 text-sm leading-7 text-stone-600">{{ item.description }}</p>
            </article>
          </div>
        </div>
      </section>

      <section class="bg-white py-12 md:py-16">
        <div class="mx-auto max-w-7xl px-4">
          <div v-scroll-reveal class="mb-8 text-center">
            <div class="text-sm font-semibold uppercase tracking-[0.24em] text-brand-600">{{ content.customers.badge }}</div>
            <h2 class="mt-3 text-3xl font-semibold text-stone-900 md:text-4xl">{{ content.customers.heading }}</h2>
            <p class="mx-auto mt-4 max-w-3xl text-base leading-8 text-stone-600 md:text-lg">{{ content.customers.description }}</p>
          </div>

          <div v-scroll-reveal-stagger class="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <div v-for="group in content.customers.items" :key="group.title" class="rounded-[2rem] border border-stone-200 bg-stone-50 p-6 shadow-soft">
              <div class="text-lg font-semibold text-stone-900">{{ group.title }}</div>
              <ul class="mt-4 grid gap-2 text-sm text-stone-600">
                <li v-for="point in group.points" :key="point" class="flex gap-2">
                  <span class="text-brand-500">•</span>
                  <span>{{ point }}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section class="mx-auto max-w-7xl px-4 py-12 md:py-16">
        <div v-scroll-reveal="'scale-up'" class="overflow-hidden rounded-[2rem] bg-gradient-to-r from-brand-500 via-orange-500 to-red-500 px-6 py-12 text-white shadow-float md:px-10 md:py-16">
          <h2 class="text-3xl font-semibold md:text-5xl">{{ content.cta.heading }}</h2>
          <p class="mx-auto mt-4 max-w-2xl text-base leading-8 text-white/90 md:text-lg">{{ content.cta.description }}</p>
          <div class="mt-8 flex flex-wrap gap-3">
            <RouterLink to="/" class="rounded-full bg-white px-6 py-3.5 font-semibold text-brand-700 shadow-soft transition hover:bg-orange-50">
              {{ content.cta.primary }}
            </RouterLink>
            <a href="/#contact" class="rounded-full border border-white/25 bg-white/10 px-6 py-3.5 font-semibold text-white transition hover:bg-white/15">
              {{ content.cta.secondary }}
            </a>
          </div>
        </div>
      </section>
    </main>

    <SiteFooter :nav-items="navItems" />
  </div>
</template>

<script setup>
import { computed, onMounted, watch } from "vue";
import { RouterLink } from "vue-router";
import { useI18n } from "vue-i18n";
import AppIcon from "../components/AppIcon.vue";
import SiteFooter from "../components/SiteFooter.vue";
import SiteHeader from "../components/SiteHeader.vue";

const { locale } = useI18n();

const navItems = [
  { key: "home", label: { th: "หน้าแรก", en: "Home" }, href: "/", to: "/" },
  { key: "about", label: { th: "เกี่ยวกับเรา", en: "About" }, href: "/about", to: "/about" },
  { key: "services", label: { th: "บริการ", en: "Services" }, href: "/#services" },
  { key: "works", label: { th: "ผลงาน", en: "Works" }, href: "/#works" },
  { key: "contact", label: { th: "ติดต่อเรา", en: "Contact" }, href: "/#contact" }
];

const aboutContent = {
  th: {
    hero: {
      badge: "About Thitima Panich",
      heading: "ผู้เชี่ยวชาญด้านการจัดหาอุปกรณ์ดับเพลิง เซฟตี้ และวัสดุสนับสนุนสำหรับองค์กร",
      subheading: "ให้คำแนะนำและจัดหาอุปกรณ์ที่เหมาะกับหน้างานจริง ด้วยความเข้าใจการใช้งาน ความพร้อมในการประสานงาน และการดูแลต่อเนื่อง",
      description:
        "ฐิติมา พาณิชย์ ให้บริการด้านอุปกรณ์ดับเพลิง อุปกรณ์เซฟตี้ กู้ชีพ กู้ภัย วัสดุสำนักงาน ครุภัณฑ์สำนักงาน อุปกรณ์จราจร และของใช้ในครัวเรือน สำหรับสำนักงาน อาคาร โรงงาน โรงเรียน และหน่วยงานต่าง ๆ โดยมุ่งเน้นการคัดเลือกอุปกรณ์ให้เหมาะกับลักษณะงาน พื้นที่ใช้งาน และความต้องการของลูกค้าแต่ละแห่ง",
      ctaPrimary: "กลับหน้าแรก",
      ctaSecondary: "ติดต่อทีมงาน",
      metrics: [
        { value: "6+ หมวด", title: "กลุ่มงานหลัก", description: "ครอบคลุมงานความปลอดภัย งานสำนักงาน และอุปกรณ์สนับสนุนที่ใช้งานจริง" },
        { value: "องค์กร", title: "กลุ่มลูกค้าหลัก", description: "ดูแลงานสำหรับบริษัท โรงงาน โรงเรียน และหน่วยงานที่ต้องการข้อมูลชัดเจน" },
        { value: "ประสานงาน", title: "พร้อมให้คำแนะนำ", description: "ช่วยคัดหมวดงาน ประเมินความต้องการเบื้องต้น และแนะนำรายการที่เหมาะสม" },
        { value: "ต่อเนื่อง", title: "บริการหลังการขาย", description: "ให้ความสำคัญกับการติดตามงาน การให้ข้อมูล และการดูแลหลังส่งมอบ" }
      ]
    },
    story: {
      badge: "Company Story",
      heading: "เราทำงานด้วยความเข้าใจหน้างานจริง และประสบการณ์ในการดูแลงานจัดหาให้หลายประเภทองค์กร",
      description:
        "ฐิติมา พาณิชย์ ให้ความสำคัญกับการทำความเข้าใจลักษณะการใช้งานจริงของลูกค้า เพื่อช่วยคัดเลือกอุปกรณ์ที่เหมาะสม ใช้งานได้ตรงวัตถุประสงค์ และสอดคล้องกับบริบทของแต่ละพื้นที่ ทั้งในสำนักงาน อาคาร โรงงาน สถานศึกษา และหน่วยงานต่าง ๆ",
      points: [
        {
          icon: "users",
          title: "เข้าใจการทำงานของลูกค้าองค์กร",
          description: "สื่อสารและประสานงานกับผู้จัดซื้อ ผู้ดูแลอาคาร เจ้าของโครงการ และหน่วยงานที่ต้องการข้อมูลชัดเจนเพื่อนำไปใช้ตัดสินใจ"
        },
        {
          icon: "shield-check",
          title: "แนะนำตามการใช้งานจริง",
          description: "คำนึงถึงประเภทพื้นที่ ความเหมาะสมของอุปกรณ์ และลักษณะการใช้งาน เพื่อให้ลูกค้าได้รายการที่เหมาะกับงานจริงมากกว่าเลือกแบบกว้าง ๆ"
        },
        {
          icon: "sparkles",
          title: "สร้างความมั่นใจด้วยการดูแลต่อเนื่อง",
          description: "ให้ความสำคัญกับการตอบคำถาม การประสานงาน และการดูแลหลังส่งมอบ เพื่อให้ลูกค้ามีผู้ช่วยงานที่ติดต่อได้จริง"
        }
      ]
    },
    scope: {
      badge: "Our Scope",
      heading: "กลุ่มบริการหลักที่ร้านดูแล",
      description: "เราดูแลทั้งงานจัดหา งานสนับสนุน และการประสานงานอย่างต่อเนื่อง รวมถึงของใช้ในครัวเรือนและสินค้าใช้งานทั่วไป โดยแยกกลุ่มบริการให้เข้าใจง่ายและตรงกับการใช้งานจริง",
      items: [
        {
          icon: "shield-check",
          title: "อุปกรณ์ดับเพลิงและเซฟตี้",
          description: "สื่อสารถึงความพร้อมในการดูแลงานที่เกี่ยวกับความปลอดภัยของอาคารและสถานประกอบการ",
          points: ["ถังดับเพลิงและอุปกรณ์ประกอบ", "ป้ายเตือนและอุปกรณ์เซฟตี้", "อุปกรณ์ช่วยเหลือในเหตุฉุกเฉิน"]
        },
        {
          icon: "store",
          title: "ครุภัณฑ์สำนักงาน",
          description: "ช่วยให้ลูกค้าเข้าใจว่าบริษัทดูแลได้ทั้งหมวดงานสำนักงานและพื้นที่ทำงาน",
          points: ["โต๊ะ เก้าอี้ ตู้เอกสาร", "ชั้นวางและอุปกรณ์จัดเก็บ", "แนวทางเลือกให้เหมาะกับพื้นที่ใช้งาน"]
        },
        {
          icon: "grid",
          title: "วัสดุสำนักงาน",
          description: "เหมาะกับการแสดงว่าธุรกิจสามารถดูแลงานเบื้องต้นที่ใช้จริงในองค์กรได้อย่างต่อเนื่อง",
          points: ["เครื่องเขียนและของใช้ประจำวัน", "อุปกรณ์ประชุม", "วัสดุสนับสนุนงานเอกสาร"]
        },
        {
          icon: "truck",
          title: "อุปกรณ์จราจร กู้ชีพ กู้ภัย",
          description: "เพิ่มมิติให้งานบริการดูครอบคลุม ทั้งภายในอาคารและพื้นที่หน้างาน",
          points: ["กรวยจราจรและแผงกั้น", "อุปกรณ์กู้ชีพกู้ภัยเบื้องต้น", "หมวดอุปกรณ์เฉพาะทางตามโจทย์ลูกค้า"]
        },
        {
          icon: "shopping-bag",
          title: "ของใช้ในครัวเรือน",
          description: "ช่วยจัดหาของใช้ในบ้าน อุปกรณ์ครัว และสินค้าใช้งานทั่วไป โดยแนะนำให้ปรึกษาก่อนเพื่อให้ได้รายการที่เหมาะกับความต้องการ",
          points: ["อุปกรณ์และของใช้ในครัว", "ของใช้ในบ้านและใช้งานประจำวัน", "รายการทั่วไปตามที่ลูกค้าต้องการ"]
        }
      ]
    },
    values: {
      badge: "Brand Values",
      heading: "หลักคิดในการให้บริการของเรา",
      description: "แนวทางการทำงานของเรายึดจากการบริการจริง คือให้คำแนะนำตรงความต้องการ เลือกอุปกรณ์อย่างเหมาะสม และประสานงานด้วยความรับผิดชอบในทุกขั้นตอน",
      items: [
        {
          title: "เข้าใจงาน",
          description: "เราเริ่มจากการฟังโจทย์และลักษณะการใช้งาน เพื่อช่วยให้ลูกค้าได้อุปกรณ์ที่ตอบโจทย์จริงและไม่เกินความจำเป็น"
        },
        {
          title: "น่าเชื่อถือ",
          description: "ให้ข้อมูลตรงไปตรงมา แนะนำตามความเหมาะสม และรักษามาตรฐานการประสานงานอย่างมืออาชีพ"
        },
        {
          title: "ดูแลต่อเนื่อง",
          description: "เมื่อลูกค้าต้องการข้อมูลเพิ่มเติมหรือมีรายละเอียดงานเพิ่มขึ้น เราพร้อมช่วยต่อยอดการประสานงานให้เดินต่อได้สะดวก"
        }
      ]
    },
    process: {
      badge: "How We Work",
      heading: "ขั้นตอนการดูแลงานของเรา",
      steps: [
        { title: "รับข้อมูลและวิเคราะห์ความต้องการ", description: "สอบถามประเภทงาน พื้นที่ใช้งาน รายการที่ต้องการ และข้อจำกัดที่เกี่ยวข้อง เพื่อให้เข้าใจโจทย์ตั้งแต่ต้น" },
        { title: "คัดหมวดอุปกรณ์และแนวทางที่เหมาะสม", description: "ช่วยจัดกลุ่มรายการให้ชัด พร้อมเสนอแนวทางที่เหมาะกับลักษณะงานจริงของลูกค้า" },
        { title: "ประสานงานและให้ข้อมูลประกอบการตัดสินใจ", description: "ดูแลการสื่อสารให้เข้าใจง่าย รองรับการสอบถามรายละเอียดเพิ่มเติม และช่วยให้กระบวนการตัดสินใจเป็นระบบ" },
        { title: "ติดตามงานและดูแลหลังส่งมอบ", description: "ไม่จบเพียงการจัดหา แต่ให้ความสำคัญกับความต่อเนื่องของบริการและการช่วยเหลือหลังเริ่มใช้งาน" }
      ]
    },
    afterSales: {
      badge: "After Sales",
      heading: "บริการหลังการขายที่เราให้ความสำคัญ",
      description: "นอกจากการจัดหาอุปกรณ์ให้เหมาะกับงานแล้ว เรายังให้ความสำคัญกับการติดตามงาน การให้ข้อมูลเพิ่มเติม และการดูแลลูกค้าอย่างต่อเนื่องหลังการส่งมอบ",
      items: [
        {
          title: "พร้อมช่วยตอบคำถามหลังเริ่มใช้งาน",
          description: "เมื่อลูกค้ามีข้อสงสัยเพิ่มเติมเกี่ยวกับการใช้งาน รายการอุปกรณ์ หรือการขยายงานในอนาคต เรายังคงพร้อมให้ข้อมูลต่อเนื่อง"
        },
        {
          title: "ประสานงานได้ต่อเนื่อง",
          description: "เรามองว่าการบริการที่ดีต้องติดต่อได้จริง ตอบกลับได้จริง และช่วยประสานรายละเอียดงานให้เดินต่อได้อย่างราบรื่น"
        },
        {
          title: "สร้างความมั่นใจในระยะยาว",
          description: "การมีทีมงานที่ดูแลตั้งแต่ก่อนตัดสินใจไปจนถึงหลังส่งมอบ ช่วยให้ลูกค้ามั่นใจได้มากกว่าการซื้อขายแบบจบครั้งเดียว"
        }
      ]
    },
    customers: {
      badge: "Customer Groups",
      heading: "กลุ่มลูกค้าที่เราดูแล",
      description: "เราพร้อมให้บริการกับองค์กรหลายประเภทตามลักษณะการใช้งานจริงของแต่ละพื้นที่",
      items: [
        {
          title: "สำนักงานและบริษัทเอกชน",
          points: ["งานจัดหาอุปกรณ์สำนักงาน", "ครุภัณฑ์และอุปกรณ์ใช้งานประจำวัน", "การดูแลงานด้านความปลอดภัยในอาคาร"]
        },
        {
          title: "โรงงานและสถานประกอบการ",
          points: ["อุปกรณ์ดับเพลิงและเซฟตี้", "อุปกรณ์จราจรและควบคุมพื้นที่", "หมวดงานที่เกี่ยวข้องกับความปลอดภัยหน้างาน"]
        },
        {
          title: "โรงเรียนและสถาบัน",
          points: ["วัสดุสำนักงานและอุปกรณ์สนับสนุน", "งานดูแลพื้นที่ส่วนกลาง", "อุปกรณ์ฉุกเฉินและความปลอดภัยเบื้องต้น"]
        },
        {
          title: "หน่วยงานราชการและโครงการ",
          points: ["รูปแบบการสื่อสารที่เป็นทางการ", "ข้อมูลที่ใช้ประกอบการตัดสินใจ", "พร้อมประสานงานต่อเพื่อขอข้อมูลและเสนอราคา"]
        }
      ]
    },
    cta: {
      heading: "พร้อมให้คำปรึกษาและช่วยประสานงานด้านอุปกรณ์ที่เหมาะกับหน้างานของคุณ",
      description: "หากต้องการสอบถามรายละเอียดเพิ่มเติมเกี่ยวกับอุปกรณ์ดับเพลิง เซฟตี้ วัสดุสำนักงาน หรือรายการสนับสนุนอื่น ๆ สามารถติดต่อทีมงานเพื่อพูดคุยเบื้องต้นได้โดยตรง",
      primary: "ดูหน้าแรก",
      secondary: "ติดต่อเรา"
    }
  },
  en: {
    hero: {
      badge: "About Thitima Panich",
      heading: "An experienced sourcing partner for fire safety, office, and support equipment",
      subheading: "Focused on practical recommendations, coordinated support, and equipment selection that fits real operational needs",
      description:
        "Thitima Panich focuses on fire safety, office supplies, office furniture, rescue, emergency, and traffic-related categories for offices, buildings, factories, schools, and agencies.",
      ctaPrimary: "Back to Home",
      ctaSecondary: "Contact Team",
      metrics: [
        { value: "6+ Groups", title: "Core categories", description: "Covers office support, facility support, and safety-related scope" },
        { value: "Business", title: "Primary customer group", description: "Suitable for companies, factories, schools, and public-sector teams" },
        { value: "Service", title: "Support-focused", description: "Emphasizes service scope, customer care, and after-sales coordination" },
        { value: "Ongoing", title: "After-sales support", description: "Focused on follow-up, coordination, and continued customer care" }
      ]
    },
    story: {
      badge: "Company Story",
      heading: "We focus on clear communication and practical customer support",
      description:
        "This About page explains what Thitima Panich supports, who the business is suitable for, and how the team works across fire safety, office, and operational support categories.",
      points: [
        {
          icon: "users",
          title: "Built for business communication",
          description: "Suitable for procurement teams, project owners, and organizations that expect a more formal presentation style."
        },
        {
          icon: "shield-check",
          title: "Trust-oriented structure",
          description: "The wording and layout help the brand feel stable, prepared, and familiar with real operational requirements."
        },
        {
          icon: "sparkles",
          title: "Service-focused presentation",
          description: "The content highlights services, customer support, and coordination for a clear, service-first presentation."
        }
      ]
    },
    scope: {
      badge: "Our Scope",
      heading: "Main service groups we support",
      description: "The business supports sourcing, operational assistance, and ongoing coordination across organization equipment, household items, and general daily-use categories.",
      items: [
        {
          icon: "shield-check",
          title: "Fire Safety and Protection",
          description: "Communicates readiness for building, workplace, and facility safety-related categories.",
          points: ["Fire extinguishers and accessories", "Warning signs and safety support", "Emergency-related equipment groups"]
        },
        {
          icon: "store",
          title: "Office Furniture",
          description: "Helps visitors understand that the company also supports workplace and facility furnishing needs.",
          points: ["Desks, chairs, and filing cabinets", "Shelving and storage equipment", "Guidance based on actual space usage"]
        },
        {
          icon: "grid",
          title: "Office Supplies",
          description: "Represents the recurring and practical categories organizations often need in daily operations.",
          points: ["Stationery and daily-use items", "Meeting-related materials", "Document and administration support items"]
        },
        {
          icon: "truck",
          title: "Traffic, Rescue, and Site Support",
          description: "Extends the service perception beyond office interiors into operational and controlled-site needs.",
          points: ["Traffic cones and barrier items", "Basic rescue and emergency support", "Specialized categories by customer request"]
        },
        {
          icon: "shopping-bag",
          title: "Household and Kitchen Items",
          description: "We can also help source household goods, kitchen items, and general daily-use products, with consultation recommended before purchasing.",
          points: ["Kitchen tools and household supplies", "General home-use items", "Other everyday products by request"]
        }
      ]
    },
    values: {
      badge: "Brand Values",
      heading: "Service principles behind the business",
      description: "Every part of the page reflects how the business actually works: clear information, practical recommendations, and continued customer support.",
      items: [
        {
          title: "Clarity",
          description: "Visitors should quickly understand what the business supports and how the team can help with practical equipment needs."
        },
        {
          title: "Credibility",
          description: "The structure should make the business feel real, organized, and ready to support ongoing customer needs."
        },
        {
          title: "Helpful follow-up",
          description: "When customers need more information, the team can continue with clearer service details, item groups, and coordination support."
        }
      ]
    },
    process: {
      badge: "How We Work",
      heading: "How we support customers",
      steps: [
        { title: "Understand the requirement", description: "Review the type of site, operational context, and equipment scope being discussed." },
        { title: "Organize service groups", description: "Clarify whether the need falls into office, safety, facility, or field-support categories." },
        { title: "Provide information and coordination", description: "Prepare a clean information structure for follow-up discussion or quotation." },
        { title: "Reinforce ongoing support", description: "Present the brand as ready for follow-up care and after-sales style service." }
      ]
    },
    afterSales: {
      badge: "After Sales",
      heading: "After-sales support matters to us",
      description: "Beyond helping customers source suitable equipment, the business also emphasizes follow-up, information support, and continued coordination after the initial discussion.",
      items: [
        {
          title: "Use language that suggests long-term support",
          description: "The page should not only mention items or categories, but also how the business continues to support customers after delivery."
        },
        {
          title: "Make contact feel human and real",
          description: "The page should clearly feel like there is a real team behind it that can answer, coordinate, and continue the discussion."
        },
        {
          title: "Improve perceived professionalism",
          description: "When service scope, customer fit, and after-sales signals appear together, the business feels more complete and credible."
        }
      ]
    },
    customers: {
      badge: "Customer Groups",
      heading: "The kinds of customers we support",
      description: "The business is suitable for several organization types based on practical use cases and operational requirements.",
      items: [
        {
          title: "Offices and private companies",
          points: ["Office supply support", "Furniture and workplace support", "Safety-related support for facilities"]
        },
        {
          title: "Factories and industrial sites",
          points: ["Fire protection and safety-related categories", "Traffic and area-control support", "Operational safety communication"]
        },
        {
          title: "Schools and institutions",
          points: ["Office and administrative support items", "Shared area support", "Basic emergency and safety equipment"]
        },
        {
          title: "Public agencies and projects",
          points: ["Formal communication style", "Information that supports decision-making", "Ready to lead into inquiry and quotation discussions"]
        }
      ]
    },
    cta: {
      heading: "Ready to provide more information about our services",
      description: "If you would like more details about service scope, equipment groups, or after-sales support, the team is ready to help directly.",
      primary: "View Homepage",
      secondary: "Contact Us"
    }
  }
};

const content = computed(() => aboutContent[locale.value] || aboutContent.th);

const syncTitle = () => {
  document.title = locale.value === "th" ? "เกี่ยวกับเรา | ฐิติมา พาณิชย์" : "About Thitima Panich";
};

onMounted(syncTitle);
watch(locale, syncTitle);
</script>
